/**
 * Client-side token-bucket rate limiter. Callers `await waitForRateLimit(id, opts)`
 * before initiating a call the service caps; the returned promise resolves as soon
 * as a slot in the window frees up. Buckets are identified by string, so callers
 * on the same service operation share one queue without threading state through.
 */

export interface RateLimitOptions {
    /**
     * Maximum requests permitted inside a rolling {@link windowSeconds}.
     */
    readonly maxRequests: number

    /**
     * Length of the rolling window, in seconds. Requests older than this
     * fall off the bucket and free their slot.
     */
    readonly windowSeconds: number
}

interface Bucket {
    readonly options: RateLimitOptions
    /** Timestamps (ms, `performance.now()` scale) of requests still in window. */
    readonly timestamps: number[]
    /** FIFO of waiters — first spot in the queue wakes first. */
    readonly waiters: Array<() => void>
    /** Live setTimeout handle so we don't stack duplicate wake-ups. */
    scheduled: ReturnType<typeof setTimeout> | null
}

const buckets = new Map<string, Bucket>()

function getBucket(id: string, options: RateLimitOptions): Bucket {
    let bucket = buckets.get(id)
    if (bucket) return bucket
    bucket = { options, timestamps: [], waiters: [], scheduled: null }
    buckets.set(id, bucket)
    return bucket
}

function prune(bucket: Bucket, now: number): void {
    const windowMs = bucket.options.windowSeconds * 1000
    while (bucket.timestamps.length > 0 && now - bucket.timestamps[0] >= windowMs) {
        bucket.timestamps.shift()
    }
}

function drain(bucket: Bucket): void {
    bucket.scheduled = null
    const now = performance.now()
    prune(bucket, now)
    while (bucket.waiters.length > 0 && bucket.timestamps.length < bucket.options.maxRequests) {
        bucket.timestamps.push(now)
        bucket.waiters.shift()!()
    }
    if (bucket.waiters.length > 0) scheduleWake(bucket, now)
}

function scheduleWake(bucket: Bucket, now: number): void {
    if (bucket.scheduled != null) return
    const windowMs = bucket.options.windowSeconds * 1000
    // Next slot frees when the oldest timestamp ages out of the window.
    const wait =
        bucket.timestamps.length > 0 ? Math.max(0, windowMs - (now - bucket.timestamps[0])) : 0
    bucket.scheduled = setTimeout(() => drain(bucket), wait)
}

/**
 * Reserves a slot in the rate-limit bucket identified by `id`. The
 * returned promise resolves once the caller may proceed; each resolution
 * consumes one slot inside the rolling window.
 *
 * Buckets keep their options from the first caller — subsequent callers
 * for the same id inherit those options and their own {@link options}
 * argument is ignored. Pick one canonical set per bucket id.
 */
export function waitForRateLimit(id: string, options: RateLimitOptions): Promise<void> {
    const bucket = getBucket(id, options)
    const now = performance.now()
    prune(bucket, now)
    if (bucket.waiters.length === 0 && bucket.timestamps.length < bucket.options.maxRequests) {
        bucket.timestamps.push(now)
        return Promise.resolve()
    }
    return new Promise<void>((resolve) => {
        bucket.waiters.push(resolve)
        scheduleWake(bucket, now)
    })
}

/**
 * Clears every bucket. Intended for tests — production callers rely on
 * long-lived state so a page-level clear would defeat the point.
 */
export function _resetRateLimitBucketsForTest(): void {
    for (const bucket of buckets.values()) {
        if (bucket.scheduled) clearTimeout(bucket.scheduled)
    }
    buckets.clear()
}
